import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  QrCode, Loader2, Mail, Lock, Store, Check, ArrowRight, User, Utensils, ArrowLeft, Stamp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const [role, setRole] = useState(null); // null: choose, 'owner', 'user'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [phone, setPhone] = useState('');
  const [clientName, setClientName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loyaltyRef = searchParams.get('ref');

  useEffect(() => {
    if (loyaltyRef) setRole('user');
  }, [loyaltyRef]);

  const resetForm = () => {
    setEmail(''); setPassword(''); setRestaurantName(''); setPhone(''); setClientName('');
  };

  // Restaurateur registration
  const handleOwnerSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !restaurantName || !phone) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (password.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/register`, {
        email, password, restaurant_name: restaurantName, phone, plan: 'unique'
      });
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('subscription_status', res.data.subscription_status || 'trial');
      }
      setShowSuccessModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  // Client registration
  const handleClientSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !clientName) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (password.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/register-client`, {
        email, password, name: clientName
      });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success('Compte créé !');
      navigate('/my-loyalty');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <QrCode className="h-7 w-7 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-gray-900">ZOTMENU</span>
          </Link>
          <h1 className="font-heading text-3xl font-bold text-gray-900 mb-2">Créer votre compte</h1>
          <p className="text-gray-500">
            {!role ? 'Choisissez votre profil pour commencer' 
             : role === 'owner' ? 'Renseignez vos informations restaurateur'
             : 'Créez votre compte pour accéder à vos cartes de fidélité'}
          </p>
        </div>

        {/* Role Selection */}
        {!role && (
          <div className="space-y-4">
            <button onClick={() => { resetForm(); setRole('owner'); }} className="w-full group" data-testid="role-restaurateur">
              <Card className="border-2 border-gray-200 hover:border-orange-400 hover:shadow-lg transition-all rounded-2xl">
                <CardContent className="p-6 flex items-center gap-5">
                  <div className="h-14 w-14 rounded-2xl bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-500 transition-colors">
                    <Utensils className="h-7 w-7 text-orange-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="font-heading font-semibold text-lg text-gray-900">Je suis restaurateur</h3>
                    <p className="text-sm text-gray-500">Créez votre menu digital et gérez vos plats</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-orange-500 transition-colors" />
                </CardContent>
              </Card>
            </button>

            <button onClick={() => { resetForm(); setRole('user'); }} className="w-full group" data-testid="role-client">
              <Card className="border-2 border-gray-200 hover:border-blue-400 hover:shadow-lg transition-all rounded-2xl">
                <CardContent className="p-6 flex items-center gap-5">
                  <div className="h-14 w-14 rounded-2xl bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500 transition-colors">
                    <Stamp className="h-7 w-7 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="font-heading font-semibold text-lg text-gray-900">Je suis un client</h3>
                    <p className="text-sm text-gray-500">Accédez à vos cartes de fidélité et cumulez vos tampons</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-blue-500 transition-colors" />
                </CardContent>
              </Card>
            </button>

            <p className="text-center text-sm text-gray-500 pt-4">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-orange-500 hover:underline font-medium">Se connecter</Link>
            </p>
          </div>
        )}

        {/* Owner Registration */}
        {role === 'owner' && (
          <>
            <button onClick={() => setRole(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
              <ArrowLeft className="h-4 w-4" /> Retour
            </button>
            <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-4 mb-4" data-testid="trial-info-banner">
              <div className="h-10 w-10 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <Check className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-bold text-emerald-800 text-sm">30 jours d'essai gratuit — accès immédiat</p>
                <p className="text-emerald-600 text-xs">Sans engagement • Aucune carte bancaire requise • 19,90€ HT/mois ensuite</p>
              </div>
            </div>
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={handleOwnerSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="restaurantName">Nom du restaurant *</Label>
                    <div className="relative">
                      <Store className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input id="restaurantName" type="text" required placeholder="Mon Restaurant"
                        value={restaurantName} onChange={(e) => setRestaurantName(e.target.value)} className="pl-10 h-12" data-testid="register-restaurant" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone *</Label>
                    <div className="relative">
                      <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                      <Input id="phone" type="tel" required placeholder="0692 XX XX XX"
                        value={phone} onChange={(e) => setPhone(e.target.value)} className="pl-10 h-12" data-testid="register-phone" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input id="email" type="email" required placeholder="votre@email.com"
                        value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-12" data-testid="register-email" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Mot de passe *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input id="password" type="password" required placeholder="6 caractères minimum"
                        value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 h-12" data-testid="register-password" />
                    </div>
                    <p className="text-xs text-gray-500">Minimum 6 caractères</p>
                  </div>

                  <Button type="submit" disabled={loading} className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-semibold" data-testid="register-submit">
                    {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Création en cours...</> : 'Créer mon compte'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-gray-600">
                    Déjà un compte ?{' '}
                    <Link to="/login" className="text-orange-500 hover:text-orange-600 font-medium">Se connecter</Link>
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Client Registration */}
        {role === 'user' && (
          <>
            {!loyaltyRef && (
              <button onClick={() => setRole(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
                <ArrowLeft className="h-4 w-4" /> Retour
              </button>
            )}
            {loyaltyRef && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4 text-center">
                <Stamp className="h-6 w-6 text-orange-500 mx-auto mb-1" />
                <p className="text-sm font-medium text-orange-800">Rejoignez le programme de fidélité !</p>
                <p className="text-xs text-orange-600 mt-1">Créez votre compte pour cumuler vos tampons</p>
              </div>
            )}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={handleClientSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="clientName">Nom complet *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input id="clientName" type="text" required placeholder="Votre nom"
                        value={clientName} onChange={(e) => setClientName(e.target.value)} className="pl-10 h-12" data-testid="register-client-name" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="clientEmail">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input id="clientEmail" type="email" required placeholder="votre@email.com"
                        value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-12" data-testid="register-client-email" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="clientPassword">Mot de passe *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input id="clientPassword" type="password" required placeholder="6 caractères minimum"
                        value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 h-12" data-testid="register-client-password" />
                    </div>
                    <p className="text-xs text-gray-500">Minimum 6 caractères</p>
                  </div>

                  <Button type="submit" disabled={loading} className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-semibold" data-testid="register-client-submit">
                    {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Création en cours...</> : 'Créer mon compte'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-gray-600">
                    Déjà un compte ?{' '}
                    <Link to="/login" className="text-orange-500 hover:text-orange-600 font-medium">Se connecter</Link>
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Success Modal - Owner */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="success-modal">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <Check className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-3">Votre essai gratuit commence !</h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Votre compte est prêt. Profitez de <strong>30 jours d'essai gratuit</strong> avec toutes les fonctionnalités,
              sans engagement. Ensuite, 19,90€ HT/mois, résiliable à tout moment.
            </p>
            <Button onClick={() => { window.location.href = '/dashboard'; }} className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-semibold" data-testid="go-to-dashboard">
              Accéder à mon tableau de bord
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
